import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '@/stores/authStore';
import { useAdminStore } from '@/stores/adminStore';
import { Card } from '@/components/common/Card';
import { Button } from '@/components/common/Button';
import * as adminApi from '@/api/admin';

export default function AdminLoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated: isUserAuthenticated, token } = useAuthStore();
  const { setAdmin } = useAdminStore();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const from =
    (location.state as { from?: { pathname: string } })?.from?.pathname ||
    '/admin';

  const handleLogin = async () => {
    if (!isUserAuthenticated || !token) {
      setError(
        'You must be logged in to the portal first. Go to /login to authenticate.'
      );
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await adminApi.login();
      setAdmin({ email: response.admin.email, role: response.admin.role });
      navigate(from, { replace: true });
    } catch (e) {
      if (e instanceof Error) {
        setError(e.message);
      } else {
        setError('Failed to authenticate as admin');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-md-cream flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-md-graphite mb-2">
            Admin Dashboard
          </h1>
          <p className="text-md-slate">
            Sign in with your portal credentials to access admin controls.
          </p>
        </div>

        <Card>
          {error && (
            <div className="bg-md-error-bg border border-md-error text-md-error p-4 rounded mb-6 text-sm">
              {error}
            </div>
          )}

          {!isUserAuthenticated ? (
            <div className="text-center">
              <p className="text-md-slate mb-4">
                You need to log in to the portal first.
              </p>
              <Button
                variant="primary"
                onClick={() =>
                  navigate('/login', {
                    state: { from: { pathname: '/admin/login' } },
                  })
                }
              >
                Go to Login
              </Button>
            </div>
          ) : (
            <div className="text-center">
              <p className="text-md-slate mb-6">
                Authenticate as an admin to manage shared API keys and
                GitHub App configuration.
              </p>
              <Button
                variant="primary"
                fullWidth
                loading={isLoading}
                onClick={handleLogin}
              >
                Sign in as Admin
              </Button>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
